import {
  BasicTask,
  Collaborator,
  FavoriteSearch,
  FetchTaskParams,
  FullTask,
  GenericProviderState,
  OrganizedTimersEvents,
  SearchTask,
  SearchTaskBasic,
  ShortAppUser,
  TagCategory,
  UserTimer,
} from "@/types";
import { createContext } from "react";

// Context Type
export type AppContextType = {
  pageLoading: boolean;
  user: ShortAppUser;
  allAppUsers: ShortAppUser[];
  userTasks: BasicTask[];
  userTags: TagCategory[];
  userTimers: UserTimer[];
  runningTimer: UserTimer | null;
  singleTask: GenericProviderState<FullTask>;
  activeTaskIdRef: { current: number | undefined };
  precacheTasks: SearchTaskBasic[] | null;
  collaborators: Collaborator[] | null;
  favoriteSearches: FavoriteSearch[];
  pageTimers: OrganizedTimersEvents | undefined;
  fetchEveryoneTimers: boolean;
  isLockedAnimation: boolean;
  forceFetchAll: () => Promise<void>;
  toggleFetchAll: () => void;
  fetchPageTimers: (
    signal: AbortSignal,
    fetchEveryoneTimers: boolean
  ) => Promise<void>;
  startTimer: (taskID: number, taskName: string) => void;
  sendNewTimerStart: (newTimer: {
    taskID: number | null;
    timerName: string | null;
    startTime?: Date | null | undefined;
    endTime?: Date | null | undefined;
  }) => Promise<boolean>;
  stopTimer: () => void;
  resetTimer: () => void;
  updateAllTasks: (tasks: BasicTask[], force?: boolean) => void;
  removeTaskFromUserTasks: (taskID: number) => void;
  sendUpdateTimer: (timer: UserTimer) => void;
  hasProjectManager: (collaborators: number[]) => boolean;
  initialGetUserTasks: (options: {
    signal?: AbortSignal;
    force?: boolean;
  }) => Promise<void>;
  getFullTask: (taskParams: FetchTaskParams) => Promise<void>;
  getPrecacheTasks: (
    all: boolean,
    force: boolean,
    showToastSuccess?: boolean
  ) => Promise<void>;
  initialGetUserFavoriteSearches: ({
    force,
    signal,
  }: {
    force?: boolean;
    signal?: AbortSignal;
  }) => Promise<void>;
  stopLocalTimer: (taskID: number) => Promise<void>;
  initialGetUserTimers: (userID: number, force: boolean) => Promise<void>;
  continueTimer: (timer: { id: number; startTime: string }) => Promise<void>;
  updateFavoriteSearches: (searches: FavoriteSearch[]) => Promise<void>;
  initialGetAllAppUsers(force?: boolean): Promise<void>;
};

// Default State for the context
export const defaultState: AppContextType = {
  pageLoading: false,
  user: {
    id: -1,
    first_name: "",
    surname: "",
    email: "",
    timezone: "",
    profile_picture: "",
    role_name: "",
  },
  allAppUsers: [],
  userTasks: [],
  userTags: [],
  userTimers: [],
  runningTimer: null,
  singleTask: { data: null, loading: true, error: null },
  activeTaskIdRef: { current: undefined },
  precacheTasks: null,
  collaborators: null,
  favoriteSearches: [],
  pageTimers: undefined,
  fetchEveryoneTimers: false,
  isLockedAnimation: false,
  forceFetchAll: async () => {},
  toggleFetchAll: () => {},
  fetchPageTimers: async () => {},
  startTimer: () => {},
  sendNewTimerStart: async () => false,
  stopTimer: () => {},
  resetTimer: () => {},
  updateAllTasks: () => {},
  removeTaskFromUserTasks: () => {},
  sendUpdateTimer: () => {},
  hasProjectManager: () => false,
  initialGetUserTasks: async () => {},
  getFullTask: async () => {},
  getPrecacheTasks: async () => {},
  initialGetUserFavoriteSearches: async () => {},
  stopLocalTimer: async (taskID: number) => {},
  initialGetUserTimers: async () => {},
  continueTimer: async () => {},
  updateFavoriteSearches: async () => {},
  initialGetAllAppUsers: async () => {},
};

export const AppContext = createContext<AppContextType>(defaultState);
